import { isDigit, toSignedSuper, toSuper } from "../util/string-util.js";
class Prefix {
    definition;
    weight;
    constructor(definition) {
        this.definition = definition;
        this.weight = Math.pow(10.0, this.definition.exponent);
    }
    getName() {
        return this.definition.name;
    }
    getSymbol() {
        return this.definition.symbol;
    }
    getExponent() {
        return this.definition.exponent;
    }
    getWeight() {
        return this.weight;
    }
    equals(aPrefix) {
        if (aPrefix) {
            return (this.getName() === aPrefix.getName() &&
                this.getSymbol() === aPrefix.getSymbol() &&
                this.getExponent() === aPrefix.getExponent());
        }
        return false;
    }
}
const PREFIX_NONE = new Prefix({ name: 'none', symbol: '', exponent: 0 });
class Quantity {
    definition;
    static DIMENSION_NONE = "1";
    static JUXTAPOSITION = "·";
    static UNIT_SEPARATOR = "_";
    isq;
    defaultUnit;
    units;
    baseQuantities;
    symbol;
    dimension;
    dimensions;
    constructor(definition) {
        this.definition = definition;
    }
    initialize(isq) {
        this.isq = isq;
        if (this.definition.baseQuantities &&
            this.definition.baseQuantities.length > 0) {
            this.baseQuantities = this.definition.baseQuantities.map((def) => Object.assign({ quantity: this.isq.getQuantity(def.name) }, def));
            const firstBaseQuantityReference = this.baseQuantities[0];
            this.symbol = this.definition.symbol
                ? this.definition.symbol
                : firstBaseQuantityReference.quantity.symbol +
                    (firstBaseQuantityReference.dimension != 1
                        ? toSignedSuper(firstBaseQuantityReference.dimension)
                        : "");
            this.dimensions = Quantity.combineQuantities(isq, this.baseQuantities);
            this.dimension = Quantity.dimensionsToString(isq, this.dimensions);
        }
        else {
            this.baseQuantities = undefined;
            this.symbol = this.definition.symbol;
            this.dimensions = Quantity.parseDimension(isq, this.definition.dimension);
            this.dimension = Quantity.dimensionsToString(isq, this.dimensions);
        }
        if (this.baseQuantities && this.baseQuantities.length > 0) {
            this.defaultUnit = Quantity.createDefaultUnit(isq, this.definition.defaultUnit, this.baseQuantities); //a compound unit
        }
        else {
            this.defaultUnit = new Unit({
                system: isq.getDefaultSystem(),
                name: this.definition.defaultUnit?.name,
                symbol: this.definition.defaultUnit?.symbol,
                prefix: this.definition.defaultUnit?.prefix,
            }); // a base unit
        }
        this.defaultUnit.initialize(isq, this);
        this.units = this.definition.units
            ? this.definition.units?.map((unitDef) => new Unit(unitDef))
            : [];
        this.units.forEach((unit) => unit.initialize(isq, this));
        if (this.units.length === 0 || !this.units[0].equals(this.defaultUnit)) {
            this.units.unshift(this.defaultUnit);
        }
    }
    getName() {
        return this.definition.name;
    }
    getDimensionSymbol() {
        return this.definition.dimension;
    }
    getDimension() {
        return this.dimension;
    }
    isBaseQuantity() {
        return this.definition.dimension !== undefined;
    }
    equals(aQuantity) {
        if (aQuantity) {
            return this.getName() === aQuantity.getName();
        }
        return false;
    }
    getDefaultUnit() {
        return this.defaultUnit;
        //    if (this.units && this.units.length > 0) {
        //return this.units[0];
        // } else {
        //   return undefined;
        // }
    }
    getUnit(unitName) {
        //TODO change all configurations so we do not need to check for these legacy spelling errors
        if (unitName.indexOf('celcius') !== -1) {
            let warning = `Call to Quantity.getUnit() with wrong spelling. Called with ${unitName} but should be `;
            unitName = unitName.replace('celcius', 'celsius');
            console.warn(warning + unitName);
        }
        else if (unitName.indexOf('soundIntentsity') !== -1) {
            let warning = `Call to Quantity.getUnit() with wrong spelling. Called with ${unitName} but should be `;
            unitName = unitName.replace('soundIntentsity', 'soundIntensity');
            console.warn(warning + unitName);
        }
        const unit = this.units.find((unit) => unit.getName() === unitName);
        if (unit) {
            return unit;
        }
        else {
            const nameParts = unitName.split(".");
            if (nameParts.length == 4) {
                //requested unit has a prefix which may be created 'on the fly' based on a parent unit if it exists
                const baseUnitName = unitName.replace("." + nameParts[2], "");
                let u;
                for (let i = 0; i < this.units.length; i++) {
                    u = this.units[i];
                    if (u.getName() === baseUnitName) {
                        const prefixedUnit = new Unit({
                            prefix: nameParts[2],
                            parent: baseUnitName,
                        });
                        prefixedUnit.initialize(this.isq, this);
                        this.units.splice(i + 1, 0, prefixedUnit);
                        return prefixedUnit;
                    }
                }
            }
        }
        return undefined;
    }
    toString() {
        return this.symbol;
    }
    logUnits() {
        return this.units.map(unit => unit.getName()).join(', ');
    }
    static combineQuantities(isq, baseQuantityReferences) {
        const dimensions = new Array(isq.getBaseQuantityCount());
        for (let i = 0; i < dimensions.length; i++)
            dimensions[i] = 0.0;
        if (baseQuantityReferences && baseQuantityReferences.length > 0) {
            baseQuantityReferences.forEach((baseQuantityReference) => {
                for (let j = 0; j < dimensions.length; j++) {
                    dimensions[j] +=
                        baseQuantityReference.quantity.dimensions[j] *
                            baseQuantityReference.dimension;
                }
            });
        }
        return dimensions;
    }
    static dimensionsToString(isq, dimensions) {
        let result = "";
        dimensions.forEach((dimension, index) => {
            result +=
                isq.getBaseDimension(index) + (dimension != 1 ? "" + dimension : "");
        });
        return result.length > 0 ? result : Quantity.DIMENSION_NONE;
    }
    static parseDimension(isq, dimension) {
        const dimensions = new Array(isq.getBaseQuantityCount());
        for (let i = 0; i < dimensions.length; i++)
            dimensions[i] = 0.0;
        if (!dimension) {
            return dimensions;
        }
        let _dimension = dimension.trim();
        if (_dimension.length === 0 || _dimension === Quantity.DIMENSION_NONE) {
            return dimensions;
        }
        try {
            let dimensionIndex = isq.getBaseDimensionIndex(_dimension.charAt(0));
            let si = 1;
            let i;
            let c;
            for (i = 1; i < _dimension.length; i++) {
                c = _dimension.charAt(i);
                if (!isDigit(c) && c != "+" && c != "-") {
                    dimensions[dimensionIndex] +=
                        si < i ? parseInt(_dimension.substring(si, i)) : 1;
                    dimensionIndex = isq.getBaseDimensionIndex(_dimension.charAt(i));
                    si = i + 1;
                }
            }
            dimensions[dimensionIndex] +=
                si < i ? parseInt(_dimension.substring(si, i)) : 1;
            return dimensions;
        }
        catch (x) { }
        throw new Error("Dimension " + _dimension + " has an illegal format.");
    }
    static createDefaultUnit(isq, defaultUnitDefinition, baseQuantityReferences) {
        let baseName = defaultUnitDefinition
            ? defaultUnitDefinition.name
            : undefined;
        let symbol = defaultUnitDefinition
            ? defaultUnitDefinition.symbol
            : undefined;
        if (!baseName || !symbol) {
            let generatedBaseName = "";
            let generatedSymbol = "";
            const dimensions = Quantity.combineQuantities(isq, baseQuantityReferences);
            let defaultBaseUnit;
            dimensions.forEach((dimension, i) => {
                if (dimension !== 0) {
                    defaultBaseUnit = isq.getBaseQuantity(i).getDefaultUnit();
                    generatedBaseName +=
                        (generatedBaseName.length > 0 ? Quantity.UNIT_SEPARATOR : "") +
                            defaultBaseUnit.getPrefixedBaseName() +
                            (dimension !== 1 ? dimension.toString() : "");
                    generatedSymbol +=
                        (generatedSymbol.length > 0 ? Quantity.JUXTAPOSITION : "") +
                            defaultBaseUnit.getPrefixedSymbol() +
                            (dimension !== 1 ? toSuper(dimension) : "");
                }
            });
            baseName = baseName ?? generatedBaseName;
            symbol = symbol ?? generatedSymbol;
        }
        const baseUnits = baseQuantityReferences.map((baseQuantityReference) => {
            return {
                name: baseQuantityReference.quantity.getDefaultUnit().getName(),
                dimension: baseQuantityReference.dimension,
            };
        });
        if (baseUnits.length === 1 && baseUnits[0].dimension === 1) {
            throw new Error("Constructing units with existing dimensions is not allowed.");
        }
        return new Unit({
            system: isq.getDefaultSystem(),
            name: baseName,
            symbol,
            baseUnits,
        });
    }
}
export class Unit {
    definition;
    static LEVEL_BASE_10 = 10.0;
    static LEVEL_BASE_FIELD_QUANTITY = Math.E;
    static LEVEL_BASE_ROOT_POWER_QUANTITY = Math.E;
    static LEVEL_BASE_POWER_QUANTITY = Math.E * Math.E;
    offset = 0.0;
    name;
    quantity;
    prefix;
    parentUnit;
    baseUnits;
    constructor(definition) {
        this.definition = definition;
    }
    initialize(isq, quantity) {
        this.quantity = quantity;
        // get prefix (e.g. milli, kilo, tera)
        this.prefix =
            this.definition.prefix && isq
                ? isq.getPrefixes()[this.definition.prefix]
                : PREFIX_NONE;
        //determine conversion factor to apply to get this unit expressed in base unit (e.g. like Celsius expressed in Fahrenheit)
        this.definition.numerator =
            this.definition.numerator !== undefined ? this.definition.numerator : 1.0;
        this.definition.denominator =
            this.definition.denominator !== undefined
                ? this.definition.denominator
                : 1.0;
        //calculate the dimension offset (e.g. like Celsius expressed in Kelvin)
        if (this.definition.offsetNumerator === undefined &&
            this.definition.offsetDenominator === undefined) {
            this.offset = 0.0;
        }
        else {
            this.definition.offsetNumerator =
                this.definition.offsetNumerator !== undefined
                    ? this.definition.offsetNumerator
                    : 1.0;
            this.definition.offsetDenominator =
                this.definition.offsetDenominator !== undefined
                    ? this.definition.offsetDenominator
                    : 1.0;
            this.offset =
                this.definition.offsetNumerator / this.definition.offsetDenominator;
        }
        //determine parent unit (can be undefined)
        if (this.isBaseUnit()) {
            if (this.definition.parent) {
                //parent unit has been explicitly defined
                this.parentUnit = this.getQuantity().getUnit(this.definition.parent);
            }
            else {
                //get parent unit from our quantity
                this.parentUnit = this.getQuantity().getDefaultUnit();
            }
        }
        else {
            this.parentUnit = undefined;
        }
        //determine system, (base)name, symbol, numerator and denominator based on what we know until now
        if (this.parentUnit) {
            if (!this.definition.name) {
                const dExp = this.getPrefix().getExponent() -
                    this.parentUnit.getPrefix().getExponent();
                this.definition.numerator = dExp > 0.0 ? Math.pow(10.0, dExp) : 1.0;
                this.definition.denominator = dExp < 0.0 ? Math.pow(10.0, -dExp) : 1.0;
                this.definition.system = this.parentUnit.definition.system;
                this.definition.name = this.parentUnit.definition.name;
                this.definition.symbol = this.parentUnit.definition.symbol;
            }
            else {
                this.definition.system =
                    this.definition.system ?? this.parentUnit.definition.system;
                this.definition.name =
                    this.definition.name ?? this.parentUnit.definition.name;
                this.definition.symbol =
                    this.definition.symbol ?? this.parentUnit.definition.symbol;
            }
        }
        else {
            this.definition.system = this.definition.system ?? isq.getDefaultSystem();
        }
        //handle the base units
        if (!this.isBaseUnit()) {
            this.baseUnits = this.definition.baseUnits?.map((baseUnitReference) => Object.assign({ unit: isq.getUnit(baseUnitReference.name) }, baseUnitReference));
            //check if the given base units and the given quantity match up
            const baseQuantityReferences = this.baseUnits?.map((baseUnit) => {
                return {
                    name: baseUnit.unit.quantity.getName(),
                    dimension: baseUnit.dimension,
                    quantity: baseUnit.unit.quantity,
                };
            });
            if (baseQuantityReferences) {
                const dimensions = Quantity.combineQuantities(isq, baseQuantityReferences);
                const dimension = Quantity.dimensionsToString(isq, dimensions);
                if (quantity.getDimension() !== dimension) {
                    throw new Error("The combined base units have dimension " +
                        dimension +
                        " while the quantity of this unit(" +
                        this.getName() +
                        ") has dimension " +
                        quantity.getDimension() +
                        ".");
                }
            }
            if (!this.definition.name || !this.definition.symbol) {
                //generate these missing name and symbol definitions
                let generatedBaseName = "";
                let generatedSymbol = "";
                this.baseUnits?.forEach((baseUnit) => {
                    generatedBaseName +=
                        (generatedBaseName.length > 0 ? Quantity.UNIT_SEPARATOR : "") +
                            baseUnit.unit.getPrefixedBaseName() +
                            (baseUnit.dimension != 1 ? baseUnit.dimension.toString() : "");
                    generatedSymbol +=
                        (generatedSymbol.length > 0 ? Quantity.JUXTAPOSITION : "") +
                            baseUnit.unit.getPrefixedSymbol() +
                            (baseUnit.dimension != 1 ? toSuper(baseUnit.dimension) : "");
                });
                if (!this.definition.name) {
                    this.definition.name = generatedBaseName;
                }
                if (!this.definition.symbol) {
                    this.definition.symbol = generatedSymbol;
                }
            }
        }
        //determine the name to use for this unit
        this.name =
            quantity.getName() +
                "." +
                this.definition.system +
                "." +
                (!this.prefix || this.prefix.equals(PREFIX_NONE)
                    ? this.definition.name
                    : this.prefix.getName() + "." + this.definition.name);
        //check for Level unit related definitions and fix when needed
        if (this.definition.base !== undefined) {
            if (this.definition.base < 1.0) {
                throw new Error("Level unit(" +
                    this.getName() +
                    ") must have a base higher then 1.0 (given " +
                    this.definition.base +
                    ").");
            }
            this.definition.reference =
                this.definition.reference !== undefined
                    ? this.definition.reference
                    : 1.0;
        }
    }
    getName() {
        return this.name;
    }
    getQuantity() {
        return this.quantity;
    }
    getPrefix() {
        return this.prefix;
    }
    getParentUnit() {
        return this.parentUnit;
    }
    getBaseName() {
        return this.definition.name;
    }
    getPrefixedBaseName() {
        return ((this.getPrefix().equals(PREFIX_NONE) ? "" : this.getPrefix().getName()) +
            this.definition.name);
    }
    getSystem() {
        return this.definition.system;
    }
    getSymbol() {
        return this.definition.symbol;
    }
    getPrefixedSymbol() {
        return this.getPrefix().getSymbol() + this.definition.symbol;
    }
    getBaseUnitName() {
        return `${this.getQuantity().getName()}.${this.getSystem()}.${this.getBaseName()}`;
    }
    getNumerator() {
        return this.definition.numerator;
    }
    getDenominator() {
        return this.definition.denominator;
    }
    getOffset() {
        return this.offset;
    }
    getBase() {
        return this.definition.base;
    }
    isLinear() {
        return (this.definition.offsetNumerator === undefined &&
            this.definition.offsetDenominator === undefined &&
            this.definition.base === undefined);
    }
    getSIConversionFactor() {
        let cf = 1.0;
        if (!this.isLinear()) {
            throw new Error("Unit " + this.getName() + " is not linear.");
        }
        if (this.isBaseUnit()) {
            if (!this.isRootUnit()) {
                return ((this.getNumerator() / this.getDenominator()) *
                    this.getParentUnit().getSIConversionFactor());
            }
            else {
                return 1.0;
            }
        }
        else {
            return this.baseUnits.reduce((cf, baseUnit) => (cf *
                Math.pow(baseUnit.unit.getSIConversionFactor(), baseUnit.dimension) *
                this.getNumerator()) /
                this.getDenominator(), 1.0);
        }
    }
    getParentConversionFactor() {
        if (!this.isLinear()) {
            throw new Error("Unit " + this.getName() + " is not linear.");
        }
        if (this.isBaseUnit()) {
            if (!this.isRootUnit()) {
                return this.getNumerator() / this.getDenominator();
            }
            else {
                return 1.0;
            }
        }
        else {
            return this.baseUnits.reduce((cf, baseUnit) => (cf *
                Math.pow(baseUnit.unit.getSIConversionFactor(), baseUnit.dimension) *
                this.getNumerator()) /
                this.getDenominator(), 1.0);
        }
    }
    equals(aUnit) {
        if (aUnit) {
            return this.getName() === aUnit.getName();
        }
        return false;
    }
    isBaseUnit() {
        return (this.definition.baseUnits === undefined ||
            this.definition.baseUnits.length === 0);
    }
    isRootUnit() {
        return this.isBaseUnit() && this.equals(this.parentUnit);
    }
    //conversions
    toParent(value) {
        if (this.isLinear()) {
            //linear OR affine unit
            return value * this.getParentConversionFactor() + this.getOffset();
        }
        else {
            //logarithmic unit
            return Math.pow(this.definition.base, value * this.prefix.getWeight());
        }
    }
    fromParent(value) {
        if (this.isLinear()) {
            //linear OR affine unit
            return (value - this.getOffset()) / this.getParentConversionFactor();
        }
        else {
            //logarithmic unit
            if (this.definition.base === 10.0) {
                return Math.log10(value) / this.prefix.getWeight();
            }
            else {
                return (Math.log10(value) /
                    Math.log10(this.definition.base) /
                    this.prefix.getWeight());
            }
        }
    }
    getConverter(targetUnit) {
        if (this.getQuantity().equals(targetUnit.getQuantity())) {
            if (this.offset != 0.0 || targetUnit.offset != 0.0) {
                //an affine unit (lineair but with an offset)
                return Converter.getAffineConverter(this, targetUnit);
            }
            else if (this.definition.base !== undefined ||
                targetUnit.definition.base !== undefined) {
                //do something intelligent here
                if (this.definition.base !== undefined) {
                    //the next cast is a bit dodgy but we have to trust that all level units are base units
                    const parentConverter = this.getParentUnit().getConverter(targetUnit);
                    return Converter.getSourceLevelConverter(this, parentConverter);
                }
                else {
                    //the next cast is a bit dodgy but we have to trust that all level units are base units
                    const targetParentConverter = this.getConverter(targetUnit.getParentUnit());
                    return Converter.getTargetLevelConverter(targetParentConverter, targetUnit);
                }
            }
            else {
                const sourceSIConversionFactor = this.getSIConversionFactor();
                const targetSIConversionFactor = targetUnit.getSIConversionFactor();
                return Converter.getConverter(this, targetUnit, sourceSIConversionFactor / targetSIConversionFactor);
            }
        }
        else {
            throw new Error("The given target unit does not belong to the same quantity as this unit (" +
                this.getName() +
                " belongs to quantity " +
                this.getQuantity() +
                "; target unit " +
                targetUnit.getName() +
                " belongs to quantity " +
                targetUnit.getQuantity() +
                ")).");
        }
    }
    getFactor() {
        return this.getConverter(this.getQuantity().getDefaultUnit()).getGain();
    }
    toBase(thisUnitvalue) {
        return thisUnitvalue * this.getFactor();
    }
    fromBase(baseUnitValue) {
        return baseUnitValue / this.getFactor();
    }
    toDefault(thisUnitValue) {
        return this.getConverter(this.getQuantity().getDefaultUnit()).convert(thisUnitValue);
    }
    getTestValue() {
        return (this.offset === 0.0 && !this.getBase() ? 1.0 : 0.0);
    }
    toString(value) {
        if (value === undefined) {
            return this.getPrefix().getSymbol() + this.getSymbol();
        }
        else {
            return value.toString() + this.getPrefix().getSymbol() + this.getSymbol();
        }
    }
}
export class ISQ {
    definition;
    prefixes;
    quantities;
    BASE_QUANTITIES;
    BASE_DIMENSIONS;
    constructor(definition) {
        this.definition = definition;
        //construct all prefixes
        this.prefixes = this.definition.prefixes
            .map((prefixDef) => new Prefix(prefixDef))
            .reduce((map, prefix) => {
            map[prefix.getName()] = prefix;
            return map;
        }, {});
        //construct all quantities
        this.quantities = this.definition.quantities
            .map((quantityDef) => new Quantity(quantityDef))
            .reduce((map, quantity) => {
            map[quantity.getName()] = quantity;
            return map;
        }, {});
        //filter out all base quantities
        this.BASE_QUANTITIES = [];
        this.BASE_DIMENSIONS = [];
        Object.values(this.quantities)
            .filter((quantity) => quantity.isBaseQuantity())
            .forEach((baseQuantity) => {
            this.BASE_QUANTITIES.push(baseQuantity);
            this.BASE_DIMENSIONS.push(baseQuantity.getDimensionSymbol());
        });
        //initialize all quantities
        Object.values(this.quantities).forEach((quantity) => quantity.initialize(this));
    }
    getName() {
        return this.definition.name;
    }
    getDefaultSystem() {
        return this.definition.defaultSystem;
    }
    getPrefixes() {
        return this.prefixes;
    }
    getQuantity(quantityName) {
        let quantity = this.quantities[quantityName];
        if (quantity == null)
            throw new Error("Illegal quantity name " +
                quantityName +
                ". Quantity " +
                quantityName +
                " could not be found.");
        return quantity;
    }
    getUnit(unitName) {
        //TODO change all configurations so we do not need to check for these legacy spelling errors
        if (unitName.indexOf('celcius') !== -1) {
            let warning = `Call to ISQ.getUnit() with wrong spelling. Called with ${unitName} but should be `;
            unitName = unitName.replace('celcius', 'celsius');
            console.warn(warning + unitName);
        }
        else if (unitName.indexOf('soundIntentsity') !== -1) {
            let warning = `Call to ISQ.getUnit() with wrong spelling. Called with ${unitName} but should be `;
            unitName = unitName.replace('soundIntentsity', 'soundIntensity');
            console.warn(warning + unitName);
        }
        const quantityName = unitName.split(".")[0];
        const quantity = this.quantities[quantityName];
        if (!quantity)
            throw new Error("Illegal unit name " +
                unitName +
                ". Quantity " +
                quantityName +
                " could not be found.");
        const unit = quantity.getUnit(unitName);
        if (!unit)
            throw new Error("Illegal unit name " +
                unitName +
                ". Unit could not be found in quantity " +
                quantityName +
                ".");
        return unit;
    }
    getBaseQuantityCount() {
        return this.BASE_QUANTITIES.length;
    }
    getBaseQuantity(index) {
        return this.BASE_QUANTITIES[index];
    }
    getBaseDimension(index) {
        return this.BASE_DIMENSIONS[index];
    }
    getBaseDimensionIndex(baseDimension) {
        for (let i = 0; i < this.BASE_DIMENSIONS.length; i++) {
            if (this.BASE_DIMENSIONS[i] === baseDimension)
                return i;
        }
        throw new Error("Given dimension " + baseDimension + " is no base dimension.");
    }
}
export class Converter {
    sourceUnit;
    targetUnit;
    gain;
    offset;
    offsets;
    converter;
    constructor() { }
    static getConverter(sourceUnit, targetUnit, gain) {
        const converter = new Converter();
        converter.sourceUnit = sourceUnit;
        converter.targetUnit = targetUnit;
        converter.gain = gain;
        converter.offset = 0.0;
        converter.offsets = undefined;
        converter.converter = undefined;
        return converter;
    }
    static getSourceLevelConverter(sourceLevelUnit, parentConverter) {
        const converter = new Converter();
        converter.sourceUnit = sourceLevelUnit;
        converter.targetUnit = parentConverter.targetUnit;
        converter.gain = 1.0;
        converter.offset = 0.0;
        converter.offsets = undefined;
        converter.converter = parentConverter;
        return converter;
    }
    static getTargetLevelConverter(targetParentConverter, targetLevelUnit) {
        const converter = new Converter();
        converter.sourceUnit = targetParentConverter.sourceUnit;
        converter.targetUnit = targetLevelUnit;
        converter.gain = 1.0;
        converter.offset = 0.0;
        converter.offsets = undefined;
        converter.converter = targetParentConverter;
        return converter;
    }
    static getAffineConverter(sourceUnit, targetUnit) {
        //!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
        //this implementation assumes the source and target units to be Base units.
        //The only quantity that has affine units (linear but with an offset)is the
        //thermodynamic Temperature :  Fahrenheit/Celsius vs Kelvin/Rankie. All these
        //units are base units
        if (sourceUnit.getQuantity().equals(targetUnit.getQuantity())) {
            if (sourceUnit.getName() === targetUnit.getName()) {
                const converter = new Converter();
                converter.converter = undefined;
                converter.sourceUnit = sourceUnit;
                converter.gain = 1.0;
                converter.offset = 0.0;
                converter.targetUnit = sourceUnit;
                converter.offsets = [converter.offset];
                return converter;
            }
            else {
                const unitChain = []; //new Array(5);
                const inverseUnitChain = []; //new Array(5);
                let unit = sourceUnit;
                let parentUnit = unit.getParentUnit();
                unitChain.push(unit);
                while (!parentUnit.equals(targetUnit) &&
                    !parentUnit.equals(targetUnit.getParentUnit()) &&
                    !parentUnit.isRootUnit() &&
                    unit.getQuantity().getDimension() ===
                        parentUnit.getQuantity().getDimension()) {
                    unit = parentUnit;
                    unitChain.push(unit);
                    parentUnit = unit.getParentUnit();
                }
                const lastUnit = unit;
                const lastUnitParent = parentUnit;
                if (!parentUnit.equals(targetUnit)) {
                    unit = targetUnit;
                    parentUnit = unit.getParentUnit();
                    inverseUnitChain.push(unit);
                    while (!parentUnit.equals(lastUnitParent) &&
                        !parentUnit.isRootUnit() &&
                        unit.getQuantity().getDimension() ===
                            parentUnit.getQuantity().getDimension()) {
                        unit = parentUnit;
                        inverseUnitChain.push(unit);
                        parentUnit = unit.getParentUnit();
                    }
                    const lastInverseUnit = unit;
                    const LastInverseUnitParent = parentUnit;
                    if (!LastInverseUnitParent.equals(lastUnitParent)) {
                        throw new Error("No path found from " +
                            sourceUnit.getName() +
                            " to " +
                            targetUnit.getName() +
                            ". SOURCE PATH : " +
                            unitChain +
                            " TARGET PATH : " +
                            inverseUnitChain);
                    }
                }
                return Converter.getChainConverter(unitChain, inverseUnitChain);
            }
        }
        else {
            throw new Error("The given source and target units do not belong to the same quantity (source unit " +
                sourceUnit.getName() +
                " belongs to quantity " +
                sourceUnit.getQuantity() +
                "; target unit " +
                targetUnit.getName() +
                " belongs to quantity " +
                targetUnit.getQuantity() +
                ")).");
        }
    }
    static getChainConverter(units, inverseUnits) {
        let unit;
        let curGain;
        let i, j;
        const chainSize = units.length;
        const inverseChainSize = inverseUnits.length;
        const converter = new Converter();
        converter.converter = undefined;
        converter.gain = 1.0;
        converter.offset = 0.0;
        converter.sourceUnit = units[0];
        converter.targetUnit =
            inverseChainSize > 0
                ? inverseUnits[0]
                : units[chainSize - 1].getParentUnit();
        converter.offsets = new Array(chainSize + inverseChainSize);
        //normal chain
        for (i = 0; i < chainSize; i++) {
            unit = units[i];
            curGain = unit.getNumerator() / unit.getDenominator();
            converter.offsets[i] = unit.getOffset();
            for (j = 0; j < i; j++) {
                converter.offsets[j] *= curGain;
            }
            converter.gain *= curGain;
        }
        //inverse chain
        for (i = 0; i < inverseChainSize; i++) {
            unit = inverseUnits[inverseChainSize - 1 - i];
            curGain = unit.getDenominator() / unit.getNumerator();
            converter.offsets[chainSize + i] = -1 * unit.getOffset() * curGain;
            for (j = 0; j < chainSize + i; j++) {
                converter.offsets[j] *= curGain;
            }
            converter.gain *= curGain;
        }
        //calculate offset;
        for (i = 0; i < converter.offsets.length; i++)
            converter.offset += converter.offsets[i];
        return converter;
    }
    convert(value) {
        if (!this.converter) {
            return value * this.gain + this.offset;
        }
        else {
            if (this.sourceUnit.getBase() !== undefined) {
                return this.converter.convert(this.sourceUnit.toParent(value));
            }
            else {
                return this.targetUnit.fromParent(this.converter.convert(value));
            }
        }
    }
    getGain() {
        return this.gain;
    }
    getOffset() {
        return this.offset;
    }
    getSourceUnit() {
        return this.sourceUnit;
    }
    getTargetUnit() {
        return this.targetUnit;
    }
    toString() {
        let sb = "";
        if (!this.converter) {
            sb += this.sourceUnit.getName();
            if (this.gain !== 1.0) {
                sb += `*${this.gain}`;
            }
            if (this.offset !== 0.0) {
                sb += `${this.offset > 0.0 ? "+" : ""}${this.offset}`;
            }
            sb += ` = ${this.targetUnit.getName()}`;
        }
        else {
            if (this.sourceUnit.getBase() !== undefined) {
                sb += `${this.sourceUnit.getBase()}^(${this.sourceUnit.getName()}*${this.sourceUnit.getPrefix().getWeight()})`;
                if (this.converter.gain !== 1.0) {
                    sb += `*${this.converter.gain}`;
                }
                if (this.converter.offset !== 0.0) {
                    sb += `${this.converter.offset > 0.0 ? "+" : ""}${this.converter.offset}`;
                }
                sb += ` = ${this.targetUnit.getName()}`;
            }
            else {
                sb += `LOG${this.targetUnit.getBase()}^(${this.sourceUnit.getName()}`;
                if (this.converter.gain !== 1.0) {
                    sb += `*${this.converter.gain}`;
                }
                if (this.converter.offset !== 0.0) {
                    sb += `${this.converter.offset > 0.0 ? "+" : ""}${this.converter.offset}`;
                }
                sb += `/${this.targetUnit.getPrefix().getWeight()} = ${this.targetUnit.getName()}`;
            }
        }
        return sb;
    }
}
